<script setup>
import Icon from '@/Components/UI/Icon.vue'
import { router } from '@inertiajs/vue3'

const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  backHref: { type: String, default: '' },
  backLabel: { type: String, default: '' },
  narrow: { type: Boolean, default: false },
})

const onBack = (e) => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    e.preventDefault()
    window.history.back()
  } else if (props.backHref) {
    e.preventDefault()
    router.visit(props.backHref)
  }
}
</script>

<template>
  <div
    :class="[
      'space-y-6',
      narrow ? 'max-w-xl mx-auto' : '',
    ]"
  >
    <!-- Breadcrumb / back — bare arrow icon by convention; backLabel only for screen readers -->
    <div v-if="$slots.breadcrumb || backHref" class="min-h-5 flex items-center gap-2 text-sm text-[color:var(--inkSoft)]">
      <slot name="breadcrumb">
        <a
          v-if="backHref"
          :href="backHref"
          :aria-label="backLabel || 'Back'"
          class="inline-flex items-center justify-center h-7 w-7 -ml-1 rounded-[var(--radius-button)] text-[color:var(--inkSoft)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surfaceAlt)] transition-colors"
          @click="onBack"
        >
          <Icon name="chev-l" :size="18" />
        </a>
      </slot>
    </div>

    <!-- Header — title lives in AppLayout topbar; only subtitle + actions render here -->
    <header
      v-if="subtitle || $slots.subtitle || $slots.actions"
      class="flex flex-wrap items-start justify-between gap-4"
    >
      <p
        v-if="subtitle || $slots.subtitle"
        class="text-sm text-[color:var(--inkSoft)] min-w-0"
      >
        <slot name="subtitle">{{ subtitle }}</slot>
      </p>
      <div v-else class="min-w-0" />
      <div v-if="$slots.actions" class="flex items-center gap-2 shrink-0">
        <slot name="actions" />
      </div>
    </header>

    <!-- Filter bar -->
    <div v-if="$slots.filters" class="filter-bar flex flex-wrap items-center gap-3">
      <slot name="filters" />
    </div>

    <!-- Main content -->
    <div class="space-y-6">
      <slot />
    </div>

    <!-- Footer -->
    <footer v-if="$slots.footer" class="pt-4 border-t border-[color:var(--line)]">
      <slot name="footer" />
    </footer>
  </div>
</template>
